import { gql, useMutation } from '@apollo/client';
import { PreSoldMembership } from '@provision/entity-types';
import { useCallback } from 'react';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { PRE_SOLD_MEMBERSHIP_FIELDS } from './pre-sold-membership.fragments';

const CREATE_PRE_SOLD_MEMBERSHIP_MUTATION = gql`
  mutation CreatePreSoldMembership($params: PreSoldMembershipDataInput!) {
    createPreSoldMembership(data: $params) {
      ...PreSoldMembershipFields
    }
  }
  ${PRE_SOLD_MEMBERSHIP_FIELDS}
`;

interface CreatePreSoldMembershipMutation {
  createPreSoldMembership: Partial<PreSoldMembership>[];
}
export const usePreSoldMembership = () => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const [createPreSoldMembershipMutation, ...rest] =
    useMutation<CreatePreSoldMembershipMutation>(CREATE_PRE_SOLD_MEMBERSHIP_MUTATION, {
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          'Something went wrong when creating the pre-sold membership, please try again',
        );
      },
    });

  const createPreSoldMembership = useCallback(
    (data: Partial<PreSoldMembership>) => {
      if (!data.addressId || !data.partnerId) return;

      return createPreSoldMembershipMutation({
        variables: {
          params: {
            ...data,
          },
        },
        onCompleted: () => {
          showSuccessAlert('Pre-sold membership created successfully');
        },
        optimisticResponse: {
          createPreSoldMembership: [
            {
              id: 'temp-pre-sold-id',
              preSoldMembershipId: 'temp-pre-sold-id',
              addressId: data.addressId,
              intervalCount: data.intervalCount || 0,
              intervalUnit: data.intervalUnit || 'month',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              startDate: new Date().toISOString(),
              partnerId: data.partnerId,
              amount: data.amount || 0,
              partnerProductId: data.partnerProductId || '',
              endDate: new Date().toISOString(),
              isScheduled: true,
            },
          ],
        },
        update: (cache, { data }) => {
          const createdPsm = data?.createPreSoldMembership?.[0];

          if (!createdPsm?.id) return;

          const id = cache.identify({
            __typename: 'Address',
            id: createdPsm.addressId,
          });

          const fields = generateFieldModifiersMap({
            preSoldMemberships: [
              {
                __typename: 'PreSoldMembership',
                ...createdPsm,
              },
            ],
          });

          cache.modify({
            id,
            fields,
          });
        },
      });
    },
    [createPreSoldMembershipMutation, showSuccessAlert],
  );

  return {
    createPreSoldMembership,
    ...rest,
  };
};
